/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import styled from 'styled-components';
import { useEffect, useRef, useState } from '@web-stories-wp/react';
import PropTypes from 'prop-types';
import {
  preloadImage,
  resourceList,
  getMediaSizePositionProps,
  calculateSrcSet,
  getSmallestUrlForWidth,
} from '@web-stories-wp/media';
import { __ } from '@web-stories-wp/i18n';
import { isValidHttpUrl } from '@src/web-stories-wp/story-editor/src/utils/isValidHttpUrl';
import LoadingIndicator from '../../components/loading-indicator/LoadingIndicator';

/**
 * Internal dependencies
 */
import StoryPropTypes from '../../types';
import { useLutImage } from '../../utils/lut/image';
import { getLutImageSource } from '../../utils/lut/source';
import { useLutFilters } from '../../app/lutFilters';

import { mediaWithScale } from './util';
import MediaDisplay from './display';

const Img = styled.img`
  position: absolute;
  ${mediaWithScale}
`;

const CenteredLoadingIndicator = styled(LoadingIndicator)`
  position: absolute;
  left: 50%;
  top: 50%;
  -webkit-transform: translate(-50%, -50%);
  transform: translate(-50%, -50%);
`;

const LoadingMask = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  left: 0;
  top: 0;
  background: rgba(0, 0, 0, 0.5);
`;

const LutPreviewError = styled.div`
  background: rgba(0, 0, 0, 0.8);
  bottom: 8px;
  color: ${({ theme }) => theme.colors.fg.primary};
  font-size: 12px;
  left: 8px;
  line-height: 16px;
  padding: 6px 8px;
  position: absolute;
  right: 8px;
  text-align: center;
`;

function getResourceKey(resource) {
  return `${resource.id ?? ''}:${resource.src ?? ''}:${
    resource.local ? 'local' : ''
  }`;
}

function getSourceState(resource) {
  let srcType = 'smallest';
  let src = getSmallestUrlForWidth(0, resource);
  const resourceState = resourceList.get(resource.id);

  if (resourceState?.type === 'cached') {
    srcType = 'cached';
    src = resourceState.url;
  }

  if (resourceState?.type === 'fullsize' || resource.local) {
    srcType = 'fullsize';
    src = resource.src;
  }

  return {
    resourceKey: getResourceKey(resource),
    src,
    srcType,
  };
}

function getDevicePixelRatio() {
  const ratio = window.devicePixelRatio;
  return Number.isFinite(ratio) && ratio > 0 ? ratio : 1;
}

function ImageDisplay({ element, box, previewMode }) {
  const { resource, scale, focalX, focalY } = element;
  const { loading } = resource;
  const { width, height } = box;
  const ref = useRef();
  const [pixelRatio, setPixelRatio] = useState(getDevicePixelRatio);
  const resourceKey = getResourceKey(resource);

  const [sourceState, setSourceState] = useState(() =>
    getSourceState(resource)
  );

  const { src, srcType } =
    sourceState.resourceKey === resourceKey
      ? sourceState
      : getSourceState(resource);
  const srcSet = srcType === 'fullsize' ? calculateSrcSet(resource) : '';
  const imgProps = getMediaSizePositionProps(
    resource,
    width,
    height,
    scale,
    focalX,
    focalY
  );
  const lutDimensions = {
    width: Math.max(1, Math.ceil((imgProps.width || width) * pixelRatio)),
    height: Math.max(1, Math.ceil((imgProps.height || height) * pixelRatio)),
  };
  const lutSource = getLutImageSource(resource, lutDimensions.width);
  const lutVersionId =
    typeof element.lutFilter?.versionId === 'string'
      ? element.lutFilter.versionId.toLowerCase()
      : '';
  const lutIntensity = Math.min(
    100,
    Math.max(0, Number(element.lutFilter?.intensity) || 0)
  );
  const hasActiveLut = Boolean(lutVersionId && lutIntensity > 0);
  useEffect(() => {
    if (!hasActiveLut) {
      return undefined;
    }
    const updatePixelRatio = () => setPixelRatio(getDevicePixelRatio());
    // Re-arm the resolution query after each change so moving between displays
    // or browser zoom regenerates the preview even without another editor edit.
    const mediaQuery = window.matchMedia?.(`(resolution: ${pixelRatio}dppx)`);
    updatePixelRatio();
    mediaQuery?.addEventListener?.('change', updatePixelRatio);
    window.addEventListener('resize', updatePixelRatio);
    return () => {
      mediaQuery?.removeEventListener?.('change', updatePixelRatio);
      window.removeEventListener('resize', updatePixelRatio);
    };
  }, [hasActiveLut, pixelRatio]);

  const lutAsset = useLutFilters(
    ({ state }) => state.assetsByVersionId?.[lutVersionId]
  );
  const lutResolutionError = useLutFilters(
    ({ state }) => state.resolutionError
  );
  const lutImage = useLutImage(
    lutSource,
    element.lutFilter,
    lutAsset,
    lutDimensions
  );
  const lutStatus =
    hasActiveLut && !lutAsset
      ? lutResolutionError
        ? 'error'
        : 'loading'
      : lutImage.status;
  const displaySrc = lutImage.src || src;
  const displaySrcSet = lutImage.src ? '' : srcSet;

  useEffect(() => {
    setSourceState((currentSourceState) =>
      currentSourceState.resourceKey === resourceKey
        ? currentSourceState
        : getSourceState(resource)
    );
  }, [resource, resourceKey]);

  useEffect(() => {
    if (!isValidHttpUrl(resource.src)) {
      return;
    }

    let timeout;
    let mounted = true;
    if (resourceList.get(resource.id)?.type !== 'fullsize' && resource.src) {
      timeout = setTimeout(async () => {
        const preloadedImg = await preloadImage(resource.src, srcSet);

        if (mounted) {
          resourceList.set(resource.id, {
            type: 'fullsize',
          });
          setSourceState({
            resourceKey,
            src: preloadedImg.currentSrc,
            srcType: 'fullsize',
          });
        }
      });
    }
    return () => {
      mounted = false;
      clearTimeout(timeout);
    };
  }, [resource.id, resource.src, resourceKey, srcSet]);

  const showPlaceholder = srcType !== 'fullsize';

  return (
    <MediaDisplay
      element={element}
      mediaRef={ref}
      showPlaceholder={showPlaceholder}
      previewMode={previewMode}
    >
      {(displaySrc || displaySrcSet) && (
        <Img
          ref={ref}
          draggable={false}
          decoding="sync"
          src={displaySrc}
          srcSet={displaySrcSet}
          alt={resource.alt}
          data-testid="imageElement"
          data-leaf-element="true"
          data-lut-status={lutStatus}
          {...imgProps}
        />
      )}
      {!previewMode && loading && (
        <>
          <LoadingMask />
          <CenteredLoadingIndicator />
        </>
      )}
      {lutStatus === 'error' && (
        <LutPreviewError role="alert">
          {__('Unable to preview LUT filter.', 'web-stories')}
        </LutPreviewError>
      )}
    </MediaDisplay>
  );
}

ImageDisplay.propTypes = {
  element: StoryPropTypes.elements.image.isRequired,
  box: StoryPropTypes.box.isRequired,
  previewMode: PropTypes.bool,
};

export default ImageDisplay;
